import { useEffect } from "react";
import { createPortal } from "react-dom";
import { CheckCircle2, X } from "lucide-react";
import { Button } from "@/components/ui/Button";

interface SuccessPopupProps {
  open: boolean;
  onClose: () => void;
  /** Confirmation message shown to the user. */
  message?: string;
  title?: string;
}

/**
 * Small blocking confirmation popup with a green check and an OK button.
 * The app has no global toast system, so success confirmations are rendered
 * as a portal-based dialog the user must acknowledge. Styled to match the
 * brutalist system: sharp corners, mono uppercase header, hard brand shadow.
 */
export function SuccessPopup({
  open,
  onClose,
  message = "Udało się wgrać plik na system",
  title = "Sukces",
}: SuccessPopupProps) {
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      data-modal-layer
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50"
      onClick={onClose}
    >
      <div
        role="alertdialog"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
        className="bg-[var(--color-bg-surface)] border border-[var(--color-border-strong)] w-full max-w-sm flex flex-col"
        style={{ boxShadow: "6px 6px 0 var(--color-primary)" }}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--color-border)]">
          <h2 className="text-xs font-mono font-bold uppercase tracking-wider text-[var(--color-text-primary)]">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Zamknij"
            className="p-1 text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors duration-100"
          >
            <X size={16} />
          </button>
        </div>
        <div className="flex flex-col items-center gap-4 px-6 py-8 text-center">
          <CheckCircle2 className="h-12 w-12 text-green-500" strokeWidth={2} />
          <p className="text-sm font-medium leading-relaxed text-[var(--color-text-primary)]">
            {message}
          </p>
          <Button type="button" autoFocus onClick={onClose} className="mt-1 px-10">
            OK
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
